import { Tooltip, Toast, Modal, Collapse, Tab, Dropdown } from 'bootstrap/dist/js/bootstrap.esm.js';
import './js/utils';
import { initAvatars } from './js/avatar.js';
import { initAuthPasswordToggles } from './js/auth.js';

globalThis.bootstrap = { Tooltip, Toast, Modal, Collapse, Tab, Dropdown };

document.addEventListener('DOMContentLoaded', () => {
  initAvatars();
  initAuthPasswordToggles();
  initClientShellInteractions();

  /**
   * Global error handler for unhandled Promise rejections
   */
  window.addEventListener('unhandledrejection', function(event) {
    const error = event.reason;
    let message = 'An unexpected error occurred';
    if (error && typeof error === 'object') {
      message = error.message || error.code || message;
    } else if (typeof error === 'string') {
      message = error;
    }
    FOSSBilling.message(message, 'error');
  });

  /**
   * Global error handler for synchronous errors
   */
  window.onerror = function(message, source, lineno, colno, error) {
    let displayMessage = message;
    if (error && error.message) {
      displayMessage = error.message;
    }
    if (source) {
      const fileName = source.split('/').pop();
      displayMessage += ` @ ${fileName}:${lineno}:${colno}`;
    }
    FOSSBilling.message(displayMessage, 'error');
  };

  /**
   * Enable Bootstrap Tooltip
   */
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));

  /**
   * Manage flash message to show after page reload
   */
  globalThis.flashMessage = ({message = '', reload = false, type = 'info'}) => {
    let key = 'flash-message';
    let sessionMessage = sessionStorage.getItem(key);
    if (message === '' && sessionMessage) {
      FOSSBilling.message(sessionMessage, type);
      sessionStorage.removeItem(key);
      return;
    }
    if (message) {
      sessionStorage.setItem(key, message);
      if (typeof reload === 'boolean' && reload) {
        window.location.reload();
      } else if (typeof reload === 'string') {
        window.location.assign(reload);
      }
    }
  };
  flashMessage({});

  /**
   * Add asterisk to required field labels
   */
  const requiredInputs = document.querySelectorAll('input[required], textarea[required]');
  requiredInputs.forEach(input => {
    const label = input.previousElementSibling;
    const isAuth = input.parentElement.parentElement.classList.contains('auth');
    if (!isAuth && label && label.tagName.toLowerCase() === 'label') {
      const asterisk = document.createElement('span');
      asterisk.textContent = ' *';
      asterisk.classList.add('text-danger');
      label.appendChild(asterisk);
    }
  });

  const currencySelector = document.querySelectorAll('select.currency_selector');
  currencySelector.forEach(function (select) {
    select.addEventListener('change', function () {
      API.guest.post('cart/set_currency', {currency: select.value}, function(response) {
        location.reload()
      }, function(error) {
        FOSSBilling.message(error)
      });
    });
  });

  /**
   * Lazy load Tom Select only if language selector exists
   * Includes error handling and ensures CSS is loaded before JS initializes
   */
  const languageSelector = document.querySelector('.js-language-selector');
  if (languageSelector) {
    // Dynamically import TomSelect module with error handling
    import('./js/tomselect.js')
      .then(module => {
        if (typeof module.default === 'function') {
          module.default();
        } else {
          console.error('TomSelect module does not export a default function');
        }
      })
      .catch(err => {
        console.error('Failed to load language selector:', err);
      });
  }

  // Attach event listeners to all forms and links with data-fb-api attribute.
  if (document.querySelector("form[data-fb-api]")) {
    API._apiForm();
  };
  if (document.querySelector("a[data-fb-api]")) {
    API._apiLink();
  }
});

function initClientShellInteractions() {
  const root = document.documentElement;
  const body = document.body;
  const storedTheme = localStorage.getItem('huraga-theme');

  if (storedTheme) {
    root.setAttribute('data-bs-theme', storedTheme);
  }

  document.querySelectorAll('[data-client-sidebar-menu]').forEach(toggle => {
    toggle.addEventListener('click', () => {
      const item = toggle.closest('.offcanvas-item');
      const isOpen = item?.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });

  document.querySelectorAll('[data-client-sidebar-group]').forEach(toggle => {
    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const group = toggle.closest('.client-submenu-group');
      const isOpen = group?.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  });

  document.querySelectorAll('[data-client-sidebar-collapse]').forEach(toggle => {
    toggle.addEventListener('click', event => {
      if (window.matchMedia('(min-width: 768px)').matches) {
        event.preventDefault();
        event.stopPropagation();
        body.classList.toggle('client-sidebar-collapsed');
      }
    }, true);
  });

  const searchToggle = document.querySelector('[data-client-search-toggle]');
  const searchPanel = document.querySelector('[data-client-search-panel]');
  const searchInput = searchPanel?.querySelector('input');

  searchToggle?.addEventListener('click', event => {
    event.stopPropagation();
    closeClientNotifications();
    searchPanel?.classList.toggle('is-visible');
    if (searchPanel?.classList.contains('is-visible')) {
      searchInput?.focus();
    }
  });

  const notificationToggle = document.querySelector('[data-client-notification-toggle]');
  const notificationPanel = document.querySelector('[data-client-notification-panel]');

  notificationToggle?.addEventListener('click', event => {
    event.stopPropagation();
    closeClientSearch();
    const isVisible = notificationPanel?.classList.toggle('is-visible') || false;
    notificationToggle.setAttribute('aria-expanded', isVisible ? 'true' : 'false');
  });

  document.querySelector('[data-client-theme-toggle]')?.addEventListener('click', () => {
    const nextTheme = root.getAttribute('data-bs-theme') === 'dark' ? 'light' : 'dark';
    root.setAttribute('data-bs-theme', nextTheme);
    localStorage.setItem('huraga-theme', nextTheme);
  });

  document.addEventListener('click', event => {
    if (!event.target.closest('[data-client-search-panel], [data-client-search-toggle]')) {
      closeClientSearch();
    }

    if (!event.target.closest('[data-client-notification-panel], [data-client-notification-toggle]')) {
      closeClientNotifications();
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      closeClientSearch();
      closeClientNotifications();
    }
  });
}

function closeClientSearch() {
  document.querySelector('[data-client-search-panel]')?.classList.remove('is-visible');
}

function closeClientNotifications() {
  document.querySelector('[data-client-notification-panel]')?.classList.remove('is-visible');
  document.querySelector('[data-client-notification-toggle]')?.setAttribute('aria-expanded', 'false');
}
